import classNames from 'classnames'
import { ReactElement, useRef, useState } from 'react'
import { ContactNameIcon } from '../contacts/ContactNameIcon'
import { LazyLocationCell } from '../LazyLocationCell'
import Dots from './styled/Dots'
import { Dropdown } from './styled/Dropdown'
import { InputSearch } from './styled/InputSearch'
import type { ContactJson, ContactType } from 'app/types/entities'

type Props = {
  buttonChild?: ReactElement
  contacts?: (ContactJson | ContactType)[]
  isLoading?: boolean
  onSearch: (search: string) => void
  onSelect: (contact: ContactJson | ContactType) => void
}
/**
 * Styled component that renders a search input and a list of contacts that can be selected
 * @param buttonChild React element for the button
 * @param contacts list of contacts to render
 * @param isLoading boolean to show loading state and render dots animation
 * @param onSearch function to search contacts, it is a callback that returns a text input
 * @param onSelect function to select a contact, it is a callback that returns a contact
 * @returns Contacts that can be searched and selected
 */
export function ContactsSearch({ buttonChild, contacts, isLoading, onSearch, onSelect }: Props): ReactElement {
  const buttonRef = useRef<HTMLDivElement>(null)
  const [current, setCurrent] = useState<string>(``)

  return (
    <Dropdown
      placement="bottom-start"
      className="min-w-72 rounded-md"
      button={
        <div ref={buttonRef}>{buttonChild ?? <span className="text-ui-sm text-strong">Search contacts</span>}</div>
      }
    >
      <div className="w-full shadow-elevation-5">
        <InputSearch onSearch={onSearch} />
        <div className="max-h-80 overflow-auto border-t border-default">
          {isLoading ? (
            <Dots />
          ) : (
            <div className="mt-1 flex-1 basis-0 flex-col items-start justify-start gap-2 rounded-md">
              {contacts?.map((c) => (
                <button
                  key={c.id}
                  className={classNames(`flex w-full gap-x-2 px-2 py-2`, {
                    [`hover:bg-surface-muted`]: current === c.id,
                  })}
                  onMouseOver={() => setCurrent(c.id!)}
                  onClick={() => {
                    onSelect(c)
                    buttonRef.current?.click()
                  }}
                >
                  <ContactNameIcon heightClassName="h-8" widthClassName="w-8" contact={c as ContactJson} />
                  <div className="flex flex-col justify-between">
                    <span className="text-left text-ui-sm text-strong">{c.name}</span>
                    <LazyLocationCell locationId={c.locationId!} className="text-left text-body-xs text-subtle" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Dropdown>
  )
}
